import { useCallback, useEffect, useRef } from 'react';

/**
 * Matches `TURNSTILE_TOKEN_HEADER` in `apps/api/src/turnstile/turnstile.service.ts`.
 */
export const TURNSTILE_TOKEN_HEADER = 'x-turnstile-token';

/**
 * Why no token rode along. None of these stops the submit (plan §2, locked
 * decision 11). `script-load-failed` means blocked or dropped, and nothing
 * else may land there: it is the number the enforcement decision turns on.
 * `timeout` means the patient pressed Send first — the click is the deadline.
 * An unset site key is not a failure and reports nothing.
 */
export type TurnstileFailure =
  | 'script-load-failed'
  | 'render-failed'
  | 'timeout';

export interface TurnstileResult {
  token?: string;
  failure?: TurnstileFailure;
}

interface TurnstileRenderOptions {
  sitekey: string;
  callback: (token: string) => void;
  'error-callback': () => void;
  'expired-callback': () => void;
}

interface TurnstileApi {
  render: (
    container: HTMLElement,
    options: TurnstileRenderOptions
  ) => string | undefined;
  reset: (widgetId: string) => void;
  remove: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
    [TURNSTILE_ONLOAD]?: () => void;
  }
}

/**
 * Cloudflare's explicit-render handshake. The API object is not guaranteed to
 * be installed when the element fires `load`, and a slow install must not read
 * as a blocked script.
 */
const TURNSTILE_ONLOAD = '__ehTurnstileOnLoad';

const SCRIPT_SRC = `https://challenges.cloudflare.com/turnstile/v0/api.js?onload=${TURNSTILE_ONLOAD}&render=explicit`;

/** A blocker that swallows the request without firing `onerror` never settles. */
const SCRIPT_LOAD_TIMEOUT_MS = 6_000;

/** Read late so `vi.stubEnv` in tests, and an unset key, both behave. */
const siteKey = () => import.meta.env.VITE_TURNSTILE_SITE_KEY ?? '';

class TurnstileLoadError extends Error {
  constructor(readonly reason: TurnstileFailure) {
    super(reason);
    this.name = 'TurnstileLoadError';
  }
}

let scriptLoad: Promise<TurnstileApi> | null = null;

function loadTurnstileScript(): Promise<TurnstileApi> {
  if (scriptLoad) return scriptLoad;
  scriptLoad = new Promise<TurnstileApi>((resolve, reject) => {
    const fail = (reason: TurnstileFailure) => {
      clearTimeout(timer);
      // Don't cache one bad network moment for the whole session.
      scriptLoad = null;
      reject(new TurnstileLoadError(reason));
    };

    const settle = () => {
      clearTimeout(timer);
      const api = window.turnstile;
      if (api) resolve(api);
      else fail('render-failed');
    };

    const timer = setTimeout(
      () => fail('script-load-failed'),
      SCRIPT_LOAD_TIMEOUT_MS
    );
    window[TURNSTILE_ONLOAD] = settle;

    const script = document.createElement('script');
    script.src = SCRIPT_SRC;
    script.async = true;
    // If the callback name ever stops being honoured, an already-installed API
    // still resolves here.
    script.onload = settle;
    script.onerror = () => fail('script-load-failed');
    document.head.appendChild(script);
  });
  return scriptLoad;
}

/**
 * Runs the invisible widget in the background while `active`. `takeToken` is
 * synchronous and never waits, so a slow or blocked challenge costs a data
 * point rather than a booking.
 */
export function useBookingTurnstile(active: boolean) {
  const containerRef = useRef<HTMLDivElement>(null);
  const tokenRef = useRef<string | undefined>(undefined);
  const failureRef = useRef<TurnstileFailure | undefined>(undefined);
  const widgetRef = useRef<{ api: TurnstileApi; id: string } | null>(null);

  useEffect(() => {
    const key = siteKey();
    if (!active || !key) return;
    const container = containerRef.current;
    if (!container) return;

    let cancelled = false;

    void loadTurnstileScript()
      .then(api => {
        if (cancelled) return;
        try {
          const id = api.render(container, {
            sitekey: key,
            callback: token => {
              tokenRef.current = token;
              failureRef.current = undefined;
            },
            'error-callback': () => {
              failureRef.current = 'render-failed';
            },
            'expired-callback': () => {
              // Tokens last 300s; a patient re-reading the summary outlasts one.
              tokenRef.current = undefined;
              const widget = widgetRef.current;
              if (widget) widget.api.reset(widget.id);
            },
          });
          if (id) widgetRef.current = { api, id };
        } catch {
          failureRef.current = 'render-failed';
        }
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        failureRef.current =
          error instanceof TurnstileLoadError
            ? error.reason
            : 'script-load-failed';
      });

    return () => {
      cancelled = true;
      tokenRef.current = undefined;
      failureRef.current = undefined;
      const widget = widgetRef.current;
      widgetRef.current = null;
      // Third-party script, and this runs on every screen change: it must not
      // throw into React's cleanup.
      try {
        widget?.api.remove(widget.id);
      } catch {
        // Already gone.
      }
    };
  }, [active]);

  /**
   * Consumes it. Tokens are single-use and the wizard leaves a working Send
   * button after a 4xx, so reusing one would score a real patient as
   * `timeout-or-duplicate` — the abuse alarm's numerator.
   */
  const takeToken = useCallback((): TurnstileResult => {
    if (!siteKey()) return {};
    const token = tokenRef.current;
    if (!token) return { failure: failureRef.current ?? 'timeout' };

    tokenRef.current = undefined;
    try {
      if (widgetRef.current) widgetRef.current.api.reset(widgetRef.current.id);
    } catch {
      // A widget that will not re-arm just means the retry goes tokenless.
    }
    return { token };
  }, []);

  // No site key: nothing loaded, nothing rendered, pre-M9 behavior.
  return { containerRef, takeToken, enabled: !!siteKey() };
}
